import Typo from "@/src/components/atom/Typo";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import { CELL_TYPE_UNDER_LINE_LINK_ACTION } from "@/src/utils/row-data-util";
import { ColDef, ValueGetterParams } from "ag-grid-community";
import dayjs from "dayjs";
import { PAYMENT_STATUS_STRINGS } from "../constant";
import { formatCreditCardMasking } from "@/src/utils/format";
import { getFormattedTimezoneDateWithOffset } from "@/src/hooks/useCompanyLocalDate";
import { calculateDisplayPrice, calculateTax, calculateUnitPrice } from "../utils/calculateTax";

export const columnPaymentList: ColDef[] = [
  {
    headerClass: "ag-right-aligned-cell ag-header-hide",
    cellStyle: { textAlign: "center" },
    field: "createdAt",
    sortable: false,
    minWidth: 104,
    cellRenderer: (params: ValueGetterParams) => {
      const gmtOffset = params.data?.gmtOffset;

      return params.data.createdAt ? (
        <Typo typoType="b9r">
          {getFormattedTimezoneDateWithOffset({
            date: params.data.createdAt,
            gmtOffset,
          })}
        </Typo>
      ) : (
        "-"
      );
    },
  },
  {
    headerClass: "ag-right-aligned-cell ag-header-hide",
    cellStyle: { textAlign: "center" },
    field: "refundDateAt",
    sortable: false,
    minWidth: 104,
    cellRenderer: (params: ValueGetterParams) => {
      return params.data.refund?.refundDateAt ? (
        <Typo typoType="b9r">
          {dayjs(params.data.refund?.refundDateAt).format(
            DATE_FORMAT_STRINGS.YYYY_MM_DD
          )}
        </Typo>
      ) : (
        "-"
      );
    },
  },
  {
    headerClass: "ag-right-aligned-cell ag-header-hide",
    cellStyle: { textAlign: "center" },
    field: "paymentDetail",
    sortable: false,
    minWidth: 140,
    cellRenderer: (params: ValueGetterParams) => {
      return params.data.paymentDetail ? (
        <Typo typoType="b9r">{params.data.paymentDetail}</Typo>
      ) : (
        "-"
      );
    },
  },
  {
    headerClass: "ag-right-aligned-cell ag-header-hide",
    cellStyle: { textAlign: "center" },
    field: "paymentStatus",
    sortable: false,
    minWidth: 140,
    cellRenderer: (params: ValueGetterParams) => {
      const t = params.data.t;

      if (params.data.paymentStatus === "PAYMENT_FAIL") {
        return (
          <Typo typoType="b9r" color="red2">
            {t(PAYMENT_STATUS_STRINGS.cancel_success)}
          </Typo>
        );
      }

      return params.data.paymentStatus ? (
        <Typo typoType="b9r">
          {t(
            PAYMENT_STATUS_STRINGS[
              String(
                params.data.paymentStatus
              ).toLowerCase() as keyof typeof PAYMENT_STATUS_STRINGS
            ]
          )}
        </Typo>
      ) : (
        "-"
      );
    },
  },
  {
    headerClass: "ag-right-aligned-cell ag-header-hide",
    cellStyle: { textAlign: "center" },
    field: "paymentNo",
    sortable: false,
    minWidth: 140,
    cellRenderer: CELL_TYPE_UNDER_LINE_LINK_ACTION,
  },
  {
    headerClass: "ag-right-aligned-cell ag-header-hide",
    cellStyle: { textAlign: "center" },
    field: "cardNumber",
    sortable: false,
    minWidth: 140,
    cellRenderer: (params: ValueGetterParams) => {
      return params.data.paymentMethod?.cardNumber ? (
        <Typo typoType="b9r">
          {formatCreditCardMasking(params.data.paymentMethod?.cardNumber)}
        </Typo>
      ) : (
        "-"
      );
    },
  },
  {
    headerClass: "ag-right-aligned-cell ag-header-hide",
    cellStyle: { textAlign: "center" },
    field: "price",
    sortable: false,
    minWidth: 140,
    cellRenderer: (params: ValueGetterParams) => {
      const displayPrice = calculateDisplayPrice(
        Number(params.data.price),
        params.data.currencyUnit,
        Number(params.data.exchangeRate)
      );
      const unitPrice = calculateUnitPrice(displayPrice, params.data.currencyUnit);

      return params.data.price ? (
        <Typo typoType="b9r">
          {unitPrice.toLocaleString("ko-KR", {
            minimumFractionDigits: params.data.currencyUnit === "KRW" ? 0 : 2,
            maximumFractionDigits: params.data.currencyUnit === "KRW" ? 0 : 2,
          })}{" "}
          {params.data.currencyUnit}
        </Typo>
      ) : (
        "-"
      );
    },
  },
  {
    headerClass: "ag-right-aligned-cell ag-header-hide",
    cellStyle: { textAlign: "center" },
    field: "tax",
    sortable: false,
    minWidth: 140,
    cellRenderer: (params: ValueGetterParams) => {
      const displayPrice = calculateDisplayPrice(
        Number(params.data.price),
        params.data.currencyUnit,
        Number(params.data.exchangeRate)
      );
      const unitPrice = calculateUnitPrice(displayPrice, params.data.currencyUnit);
      const tax = calculateTax(displayPrice, unitPrice);

      return params.data.price ? (
        <Typo typoType="b9r">
          {tax.toLocaleString("ko-KR", {
            minimumFractionDigits: params.data.currencyUnit === "KRW" ? 0 : 2,
            maximumFractionDigits: params.data.currencyUnit === "KRW" ? 0 : 2,
          })}{" "}
          {params.data.currencyUnit}
        </Typo>
      ) : (
        "-"
      );
    },
  },
  {
    headerClass: "ag-right-aligned-cell ag-header-hide",
    cellStyle: { textAlign: "center" },
    field: "amount",
    sortable: false,
    minWidth: 140,
    cellRenderer: (params: ValueGetterParams) => {
      const displayPrice = calculateDisplayPrice(
        Number(params.data.price),
        params.data.currencyUnit,
        Number(params.data.exchangeRate)
      );

      return (
        <Typo typoType="b9r">
          {displayPrice.toLocaleString("ko-KR", {
            minimumFractionDigits: params.data.currencyUnit === "KRW" ? 0 : 2,
            maximumFractionDigits: params.data.currencyUnit === "KRW" ? 0 : 2,
          })}{" "}
          {params.data.currencyUnit}
        </Typo>
      );
    },
  },
];
